import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCheckCircle,
  FiCompass,
  FiEye,
  FiGlobe,
  FiLayers,
  FiShield,
  FiUsers,
} from "react-icons/fi";

import heroImage from "../../assets/images/career.jpg";
import insuranceLogo from "../../assets/logos/TIB.png";
import travelLogo from "../../assets/logos/TTT.png";
import holdingLogo from "../../assets/logos/thrive.jpg";
import "./Group.css";

const values = [
  {
    icon: FiShield,
    title: "Integrity",
    text: "We act responsibly, communicate clearly and protect the trust placed in every Thrive business.",
  },
  {
    icon: FiUsers,
    title: "Customer focus",
    text: "We begin with real customer needs and build services that are useful, responsive and dependable.",
  },
  {
    icon: FiLayers,
    title: "Excellence",
    text: "We set high standards, invest in capability and keep improving how our companies operate.",
  },
  {
    icon: FiGlobe,
    title: "Progress",
    text: "We embrace better ideas, practical technology and opportunities that move people and businesses forward.",
  },
];

const Group = () => {
  return (
    <main className="group-page">
      <section
        className="group-hero"
        style={{ "--group-hero-image": `url(${heroImage})` }}
        aria-labelledby="group-page-title"
      >
        <div className="group-shell group-hero__inner">
          <p className="group-eyebrow group-eyebrow--light">
            About Thrive Holdings
          </p>
          <h1 id="group-page-title">
            A focused group built for <span>long-term relevance.</span>
          </h1>
          <p>
            We bring together specialist businesses with one shared ambition:
            to deliver trusted services that improve how people and
            organisations protect, plan and progress.
          </p>
          <a className="group-button group-button--primary" href="#our-story">
            Discover our group <FiArrowRight aria-hidden="true" />
          </a>
        </div>
      </section>

      <section className="group-story" id="our-story">
        <div className="group-shell group-story__grid">
          <div className="group-heading">
            <p className="group-eyebrow">Our story</p>
            <h2>Independent expertise, strengthened by one group.</h2>
          </div>
          <div className="group-story__copy">
            <p>
              Thrive Holdings Limited provides strategic direction, governance
              and shared support to customer-focused businesses. Our role is to
              help each operating company stay focused on its market while
              benefiting from the perspective and discipline of the wider
              group.
            </p>
            <p>
              Today, our companies serve customers across insurance brokerage
              and travel management. Though their services are distinct, they
              share the same commitment to professionalism, thoughtful advice
              and dependable execution.
            </p>
          </div>
        </div>
      </section>

      <section className="group-direction">
        <div className="group-shell group-direction__grid">
          <article className="direction-card direction-card--mission">
            <FiCompass aria-hidden="true" />
            <p className="group-eyebrow">Our mission</p>
            <h2>
              To build and support businesses that deliver outstanding,
              customer-centred services.
            </h2>
            <p>
              We combine capable people, sound governance and practical
              technology to help every Thrive company serve with confidence.
            </p>
          </article>

          <article className="direction-card direction-card--vision">
            <FiEye aria-hidden="true" />
            <p className="group-eyebrow">Our vision</p>
            <h2>
              To be a respected African group known for trusted businesses and
              superior service.
            </h2>
            <p>
              Our ambition is measured by the enduring value we create for
              customers, employees, partners and the markets we serve.
            </p>
          </article>
        </div>
      </section>

      <section className="group-values">
        <div className="group-shell">
          <div className="group-heading group-heading--split">
            <div>
              <p className="group-eyebrow">What guides us</p>
              <h2>Principles we carry into every business.</h2>
            </div>
            <p>
              A shared culture gives our companies a consistent standard while
              leaving room for specialist expertise and market focus.
            </p>
          </div>

          <div className="group-values__grid">
            {values.map(({ icon: Icon, title, text }, index) => (
              <article className="group-value-card" key={title}>
                <div className="group-value-card__top">
                  <Icon aria-hidden="true" />
                  <span>0{index + 1}</span>
                </div>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="group-structure" id="companies">
        <div className="group-shell">
          <div className="group-heading group-heading--center">
            <p className="group-eyebrow">Our structure</p>
            <h2>One group. Two specialist operating companies.</h2>
            <p>
              Each business is independently focused and connected by shared
              standards, oversight and ambition.
            </p>
          </div>

          <div className="group-chart" aria-label="Thrive group structure">
            <div className="group-chart__parent">
              <img src={holdingLogo} alt="Thrive Holdings Limited" />
              <span>Strategic leadership &amp; group oversight</span>
            </div>
            <div className="group-chart__connector" aria-hidden="true" />
            <div className="group-chart__companies">
              <article className="group-company">
                <span className="group-company__sector">
                  Insurance &amp; risk advisory
                </span>
                <div className="group-company__logo">
                  <img
                    src={insuranceLogo}
                    alt="Thrive Insurance Brokers Limited"
                  />
                </div>
                <p>
                  Professional insurance intermediation, risk advisory and
                  claims support for individuals and organisations.
                </p>
                <a
                  href="https://www.insurance.thrivenig.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit company <FiArrowRight aria-hidden="true" />
                </a>
              </article>

              <article className="group-company">
                <span className="group-company__sector">Travel management</span>
                <div className="group-company__logo">
                  <img
                    src={travelLogo}
                    alt="Thrive Travels and Tours Limited"
                  />
                </div>
                <p>
                  Personalised travel planning, reservations and support for
                  leisure and corporate customers.
                </p>
                <a
                  href="https://www.travels.thrivenig.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit company <FiArrowRight aria-hidden="true" />
                </a>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className="group-promise">
        <div className="group-shell group-promise__inner">
          <div>
            <p className="group-eyebrow group-eyebrow--light">
              Our commitment
            </p>
            <h2>Growth with discipline. Service with purpose.</h2>
          </div>
          <ul>
            <li>
              <FiCheckCircle aria-hidden="true" />
              Responsible leadership and oversight
            </li>
            <li>
              <FiCheckCircle aria-hidden="true" />
              Investment in people and capability
            </li>
            <li>
              <FiCheckCircle aria-hidden="true" />
              Long-term relationships over short-term gains
            </li>
          </ul>
        </div>
      </section>

      <section className="group-next">
        <div className="group-shell group-next__inner">
          <div>
            <p className="group-eyebrow">Leadership</p>
            <h2>Meet the people responsible for our direction.</h2>
          </div>
          <div className="group-next__actions">
            <Link className="group-button group-button--dark" to="/board-of-directors">
              Board of Directors <FiArrowRight aria-hidden="true" />
            </Link>
            <Link className="group-button group-button--outline" to="/management-team">
              Management team
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Group;
